import { isValidElement, useEffect, useRef, useState } from "react";
import Tippy, { TippyProps } from "@tippyjs/react";
import { Instance } from "tippy.js";
import { createGlobalStyle } from "styled-components";
import { designSystemVars as vars } from "../tokens";
import "tippy.js/dist/tippy.css";
import "tippy.js/animations/shift-away.css";

const FieldTooltipStyle = createGlobalStyle`
  .tippy-box[data-theme~="field"] > .tippy-content {
    padding: 0;
  }
  .tippy-box[data-theme~="field"] > .tippy-arrow {
    left: ${vars.spacing.xl} !important;
    transform: none !important;
  }
  .tippy-box[data-theme~="field"][data-placement^="top"] > .tippy-arrow::before {
    border-width: 8px 6px 0;
  }
`;

interface TooltipProps {
  children: string | React.ReactNode;
  tooltip?: string | React.ReactNode;
  maxWidth?: number;
  variant?: "default" | "field";
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  overflowVisible?: boolean;
  lines?: number;
  fullWidth?: boolean;
  triggerTabIndex?: number;
  triggerAriaLabel?: string;
  observeOverflow?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  tooltip,
  maxWidth = 800,
  variant = "default",
  visible,
  onVisibleChange,
  overflowVisible = false,
  lines,
  fullWidth = true,
  triggerTabIndex,
  triggerAriaLabel,
  observeOverflow = false,
}) => {
  const isField = variant === "field";
  const [showTooltip, setShowTooltip] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const observedOverflow = observeOverflow && showTooltip;

  useEffect(() => {
    const element = contentRef.current;
    if ((tooltip && !observeOverflow) || !element) return;

    const measureOverflow = () => {
      setShowTooltip(element.scrollWidth > element.clientWidth);
    };
    measureOverflow();
    if (!observeOverflow) return;

    const observer = new ResizeObserver(measureOverflow);
    observer.observe(element);
    return () => observer.disconnect();
  }, [children, tooltip, observeOverflow, observedOverflow]);

  const isControlled = visible !== undefined;
  const shouldShowTooltip = observeOverflow
    ? showTooltip
    : tooltip || showTooltip;
  const tooltipContent = tooltip ?? children;
  const childProps = isValidElement<{
    disabled?: boolean;
    "aria-disabled"?: boolean | "true" | "false";
    "aria-busy"?: boolean | "true" | "false";
  }>(children)
    ? children.props
    : undefined;
  const hasBusyTrigger = Boolean(
    childProps?.["aria-busy"] === true || childProps?.["aria-busy"] === "true",
  );
  const hasDisabledTrigger = Boolean(
    childProps?.disabled ||
    childProps?.["aria-disabled"] === true ||
    childProps?.["aria-disabled"] === "true",
  );
  const triggerCursor = hasBusyTrigger
    ? "wait"
    : hasDisabledTrigger
      ? "not-allowed"
      : undefined;

  if (!shouldShowTooltip) {
    return (
      <div
        ref={contentRef}
        data-tooltip-trigger
        tabIndex={triggerTabIndex}
        aria-label={triggerAriaLabel}
        style={{
          whiteSpace: "nowrap",
          overflow: overflowVisible ? "visible" : "hidden",
          width: fullWidth ? "100%" : undefined,
          minWidth: observeOverflow ? 0 : undefined,
          textOverflow: "ellipsis",
          display: "inline-block",
          cursor: triggerCursor,
        }}
      >
        {children}
      </div>
    );
  }

  const baseTippyProps: Partial<TippyProps> = {
    content: (
      <div
        role="tooltip"
        style={{
          color: "var(--inzeloop-ds-color-background-surface)",
          padding: "var(--inzeloop-ds-spacing-sm)",
          fontSize: isField
            ? "var(--inzeloop-ds-font-size-sm)"
            : "var(--inzeloop-ds-font-size-14-16px)",
          fontFamily: "var(--inzeloop-ds-font-family-sans)",
          fontWeight: "var(--inzeloop-ds-font-weight-regular)",
          lineHeight: isField
            ? "var(--inzeloop-ds-line-height-sm)"
            : "var(--inzeloop-ds-line-height-21-24px)",
          ...(isField && { width: 320, boxSizing: "border-box" as const }),
          maxWidth: isField ? "calc(100vw - 32px)" : "70ch",
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "left",
        }}
      >
        {tooltipContent}
      </div>
    ),
    placement: isField ? "top-start" : "top",
    theme: isField ? "field" : undefined,
    arrow: true,
    animation: "shift-away",
    maxWidth: "100%",
    onCreate: (instance: Instance) => {
      const box = instance.popper.querySelector<HTMLElement>(".tippy-box");
      if (box) {
        box.style.backgroundColor =
          "var(--inzeloop-ds-color-action-primary-pressed)";
        box.style.borderRadius = "4px";
        box.style.boxShadow = "var(--inzeloop-ds-shadow-tooltip)";
      }
      const arrow = instance.popper.querySelector<HTMLElement>(".tippy-arrow");
      if (arrow) {
        arrow.style.color = "var(--inzeloop-ds-color-action-primary-pressed)";
      }
    },
  };

  const tippyProps: TippyProps = isControlled
    ? {
        ...baseTippyProps,
        visible,
        onClickOutside: () => onVisibleChange?.(false),
      }
    : ({
        ...baseTippyProps,
        trigger: "mouseenter focus",
      } as TippyProps);

  return (
    <>
      {isField && <FieldTooltipStyle />}
      <Tippy {...tippyProps}>
        <div
          ref={contentRef}
          data-tooltip-trigger
          tabIndex={triggerTabIndex}
          aria-label={triggerAriaLabel}
          style={{
            whiteSpace: "nowrap",
            overflow: overflowVisible ? "visible" : "hidden",
            textOverflow: "ellipsis",
            maxWidth: observeOverflow ? undefined : "70ch",
            width: observeOverflow && fullWidth ? "100%" : undefined,
            minWidth: observeOverflow ? 0 : undefined,
            cursor: triggerCursor ?? "default",
          }}
        >
          {children}
        </div>
      </Tippy>
    </>
  );
};
